import { useId, useRef, useState, type ReactNode } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Hint } from '@src/components/Hint/Hint';
import { digitsOnly, OTP_CODE_LENGTH, OtpInput } from '@src/components/OtpInput/OtpInput';
import { Stack } from '@src/components/Stack/Stack';
import { testIdFor } from '@src/components/testId';
import { Typography } from '@src/components/Typography/Typography';
import { resolveApiErrorKey } from '@src/i18n/apiError';

const OTP_MAX_ATTEMPTS = 3;

export interface OtpVerifyFailure {
  key: string;
  attemptsLeft?: number;
}

export interface OtpChallengeProps {
  challengeToken: string;
  hint: ReactNode;
  /** Returns an i18n key on failure, or null after a redirect. */
  onVerify: (challengeToken: string, code: string) => Promise<OtpVerifyFailure | null>;
  testId?: string;
}

function messageForFailure(failure: OtpVerifyFailure, t: TFunction): string {
  if (failure.key === 'otp.wrong' && failure.attemptsLeft != null) {
    return t('otp.wrongLeft', { count: failure.attemptsLeft });
  }
  return t(resolveApiErrorKey(failure.key));
}

/** Six-digit code from the email. Submits as soon as the sixth digit is typed. */
export function OtpChallenge(props: OtpChallengeProps) {
  return <OtpChallengeContent key={props.challengeToken} {...props} />;
}

function OtpChallengeContent({ challengeToken, hint, onVerify, testId }: OtpChallengeProps) {
  const { t } = useTranslation();
  const errorId = useId();
  const [code, setCode] = useState('');
  const [error, setError] = useState<OtpVerifyFailure | null>(null);
  const [locked, setLocked] = useState(false);
  const [busy, setBusy] = useState(false);
  const submitting = useRef(false);
  const remainingRef = useRef(OTP_MAX_ATTEMPTS);

  async function submit(nextCode: string) {
    if (locked || submitting.current || nextCode.length !== OTP_CODE_LENGTH) {
      return;
    }
    submitting.current = true;
    setBusy(true);
    setError(null);
    const result = await onVerify(challengeToken, nextCode);
    if (result === null) {
      return;
    }
    submitting.current = false;
    setBusy(false);
    setCode('');
    if (result.key === 'otp.locked') {
      setLocked(true);
      remainingRef.current = 0;
      setError(result);
      return;
    }
    if (result.key === 'otp.wrong') {
      const left = result.attemptsLeft ?? Math.max(0, remainingRef.current - 1);
      remainingRef.current = left;
      setError({ key: 'otp.wrong', attemptsLeft: left });
      return;
    }
    setError(result);
  }

  return (
    <Stack gap={6}>
      <Typography as="div" role="body/sm">
        {hint}
      </Typography>
      <OtpInput
        label={t('otp.label')}
        value={code}
        disabled={locked || busy}
        invalid={Boolean(error)}
        describedBy={error ? errorId : undefined}
        autoFocus
        testId={testId}
        onValueChange={(value) => {
          const next = digitsOnly(value);
          setCode(next);
          if (error && !locked) {
            setError(null);
          }
          if (next.length === OTP_CODE_LENGTH) {
            void submit(next);
          }
        }}
      />
      {error ? (
        <Hint id={errorId} testId={testIdFor(testId, 'error')}>
          {messageForFailure(error, t)}
        </Hint>
      ) : null}
    </Stack>
  );
}
